/**
 * A porta de dados: tudo que as telas sabem sobre onde os dados moram.
 *
 * `Página → componente → loader → porta → adaptador`, como pede o
 * `docs/BACKEND_HANDOFF.md`. Há dois adaptadores com a mesma interface: o do
 * Supabase e o de demonstração, em memória. A escolha é feita quando este módulo
 * carrega (`temSupabase()`, no fim do arquivo); `definirPorta` existe para os
 * testes fixarem um deles. Nenhuma tela sabe qual está em uso.
 *
 * Tudo é assíncrono desde já, mesmo com dados em memória: uma porta síncrona
 * obrigaria a reescrever cada chamada quando a rede entrar.
 */

import { temSupabase } from '@/supabase';
import { adaptadorDeDemonstracao } from './adaptadorDemo';
import { adaptadorSupabase } from './adaptadorSupabase';
import type {
    AgendaDaSemana, Agendamento, CargaDeContatos, EdicaoDeAgendamento, EdicaoDeLead, EdicaoDeMembro, NovoUsuarioDaEquipe,
    CargaDeAtendimentos, CargaDoPaciente, FichaDoPaciente, FiltroDeAtendimentos, FiltroDeContatos,
    Lead, Lembrete, MembroDaEquipe, NovoAgendamento, NovoContato, RegistroDeHistorico,
    CargaDaCaixa, CargaDeVendas, CargaDoPipeline, Conversa, FiltroDeConversas, Funil,
    CargaDoFinanceiro, CategoriaDeConta, CondicaoDeCobranca, FiltroDeVendas, FiltroDoFinanceiro, Mensagem, NovoOrcamento, NovoPagamento,
    AtendimentoEmCurso, CampoDeFicha, FichaPreenchida, ModeloDeFicha, NovaFicha, Orcamento,
    ProntuarioDoPaciente, ResumoDoPaciente, ResumoDoDia, StatusDoOrcamento, Tarefa, TipoDeContato, TipoImportavel, Titulo,
    CategoriaParaEscolha, DadosDoPrestador, PrestadorDeServico, StatusDoCanal, TipoDeServico,
    CargaDoEstoque, CategoriaDeProduto, DadosDaCategoriaDeProduto, DadosDaMarca, DadosDoFornecedor,
    DadosDoProduto, FiltroDoEstoque, Fornecedor, Marca, NovaMovimentacao, NovoProduto, Produto,
    CargaDeProcedimentos, ContextoDaReserva, DadosDoProcedimento, FiltroDeProcedimentos,
    Procedimento, ReservaDeEstoque,
    AppDaMetaDaPlataforma, CampanhaDeAnuncio, CargaDoPainelDeAnuncios, ContaDaMeta, ContaDeAnuncioDaClinica,
    CargaDeCompras, DadosDoPedido, FiltroDeCompras, PedidoDeCompra, Recebimento,
    CargaDeDocumentos, DadosDoModeloDeDocumento, Documento, FiltroDeDocumentos,
    ModeloDeDocumento, NovoDocumento, StatusDoDocumento,
    CargaDeRelatorios, FiltroDeRelatorios,
    CargaDeConfiguracoes, AbaDeConfiguracoes, DadosDaClinica, DiaDeFuncionamento,
    EdicaoDaClinica, EdicaoDaUnidade, NovaUnidade, SalaDaClinica, UnidadeDaClinica,
    AbaDeAutomacoes, Automacao, CargaDeAutomacoes, ResultadoDaExecucao,
    AbaDeMarketing, Campanha, CargaDeMarketing, DadosDaCampanha,
    DadosDoLembrete,
    CargaDaClinicaNaPlataforma, CargaDaPlataforma,
    CargaDoFaturamento, DadosDoPlano, Fatura, Plano,
    CargaDoOnboarding, CargaDoSuporte, Chamado, DadosDoChamado,
    CargaDasFlags, FlagDeModulo,
    AcaoDePareamento, ArquivoDaFicha, CanalDeIntegracao, ChaveDeIa, DadosDoCanal, PareamentoDoCanal, ProvedorDeCanalDaPlataforma, DocumentoDaBase, LinhaDeConsumoDeIa, MeuOrcamentoDeIa, OrcamentoDeIa, ProvedorDeIa, RetornoEmAberto,
    Agente, DadosDoAgente, PrioridadeDaTarefa, MensagemDoAgente, PropostaDoAgente, RespostaDoAgente,
    DadosDoFluxo, Fluxo, ResultadoDoFluxo, SimulacaoDoFluxo,
    ContatoParaEscolha, MinhaConta, OpcoesDoAgendamento, LinhaImportada, ResultadoDaImportacao,
    ChaveDeApi, ChaveDeApiCriada, CreditoDaClinica, DadosDaFaturaAvulsa, DadosDaNovaClinica, DadosDoCredito,
    DadosDoDesconto, OperadorDaPlataforma, SaudeDoBanco, SituacaoDaClinica,
    UsoDeChaveDeApi,
    CobrancaDaFatura, CobrancasCriadas, DadosDoGateway, GatewayDePagamento, InadimplenciaDaClinica,
    ProvedorDeGateway, TesteDoGateway,
} from './tipos';
import type {
    AssinaturaVistaPelaClinica, CargaDeAtendimentoECrm, CategoriaDeProcedimento, DadosDaEtiqueta, DadosDaFila,
    DadosDaMensagemRapida, DadosDoDepartamento, DadosDoPacoteDoCatalogo, PacoteDoCatalogo, PreferenciaSalva, ValorDePreferencia,
} from './cadastros';
import type { SinaisDaConversa } from './comunicacao';
import type {
    CadastrosFinanceiros, DadosDaConta, DadosDoMetodo, DetalheDoTitulo, EdicaoDeTitulo, LinhaDoOfx, LinhaDoExtratoImportada,
    NovaTransferencia, NovoTitulo, Transferencia,
} from './financeiroAvulso';
import type { EtapaEditada, ModeloDeFunil } from './funis';
import type { AlvoFiscal, CertificadoFiscal, DadosDoEmitente, EmitenteFiscal, FiltroDeNotas, NotaFiscal, PadraoNfse } from './notasFiscais';
import type { AtendimentoEmAndamento, PausaDoAtendimento, RascunhoDaFicha } from './atendimentoEmCurso';
import type { ModoDaNota, NotaDeIa, OpcoesDaNovaNota, ParteDaNota, ResumoDaConsulta } from './notaDeIa';
import type {
    AcaoDeTexto, AnexoDoBrain, ConversaDoBrain, EnvioAoBrain, IdiomaDoBrain, LinkAprendido, MensagemDoBrain, ModelosDoBrain,
    FalaGerada, PedidoDoBrain, PreferenciasDoBrain, ProjetoDoBrain, PropostaDoBrain, RespostaDoBrain,
} from './brain';
import type { DadosDoPlanoDeSaude, PlanoDeSaude } from './planosDeSaude';
import type { CabecalhoDaClinica } from './fichaImpressa';
import type { Notificacao } from './notificacoes';
import type { AgendaGoogle } from './agendaGoogle';
import type { DadosDoWebhook, EntregaDeWebhook, Webhook, WebhookCriado } from './webhooks';

export interface PortaDeDados {
    /** Carga da tela de Início. */
    resumoDoDia(): Promise<ResumoDoDia>;
    /** Semana que contém o dia informado (`YYYY-MM-DD`), de segunda a sábado. */
    agendaDaSemana(diaDeReferencia: string): Promise<AgendaDaSemana>;
    historicoDoAgendamento(id: string): Promise<RegistroDeHistorico[]>;
    lembretesDoAgendamento(id: string): Promise<Lembrete[]>;
    /**
     * Salva a edição junto com o histórico que ela gera. Os dois numa chamada
     * só porque, separados, uma falha de rede deixaria alteração sem registro —
     * e num prontuário registro que falta é registro que não aconteceu.
     */
    atualizarAgendamento(id: string, mudancas: EdicaoDeAgendamento, registros: RegistroDeHistorico[]): Promise<Agendamento>;
    alternarTarefa(id: string, concluida: boolean): Promise<Tarefa>;
    /**
     * Cria uma tarefa para a equipe.
     *
     * É o que sustenta o follow-up: sugestão sem tarefa é sugestão que ninguém
     * cobra depois.
     */
    criarTarefa(dados: { titulo: string; venceEm?: string; responsavel?: string; prioridade?: PrioridadeDaTarefa }): Promise<Tarefa>;
    criarAgendamento(dados: NovoAgendamento): Promise<Agendamento>;

    /** Uma página de contatos do tipo pedido, com os totais das três abas. */
    contatos(tipo: TipoDeContato, filtro: FiltroDeContatos): Promise<CargaDeContatos>;
    criarContato(dados: NovoContato): Promise<{ id: string }>;
    atualizarLead(id: string, mudancas: EdicaoDeLead): Promise<Lead>;
    /** Move o lead uma etapa adiante. A ordem das etapas é do domínio, não da tela. */
    avancarEtapaDoLead(id: string): Promise<Lead>;
    atualizarMembro(id: string, mudancas: EdicaoDeMembro): Promise<MembroDaEquipe>;

    /** Ficha do paciente com resumo financeiro, linha do tempo e documentos. */
    paciente(id: string): Promise<CargaDoPaciente>;
    /**
     * Salva UM campo da ficha.
     *
     * Campo a campo, e não a ficha inteira: em prontuário, mandar o registro
     * completo a cada edição sobrescreve o que outra pessoa mudou no intervalo.
     */
    atualizarCampoDaFicha<C extends keyof FichaDoPaciente>(id: string, campo: C, valor: FichaDoPaciente[C]): Promise<FichaDoPaciente>;

    /** Consultas do recorte pedido, com os indicadores do mês. */
    atendimentos(filtro: FiltroDeAtendimentos): Promise<CargaDeAtendimentos>;

    /** Caixa de entrada: conversas, canais e filas. */
    caixaDeEntrada(filtro: FiltroDeConversas): Promise<CargaDaCaixa>;
    mensagensDaConversa(conversaId: string): Promise<Mensagem[]>;
    /**
     * Grava a mensagem e devolve a conversa atualizada. O envio pelo canal é
     * responsabilidade do servidor: o navegador não fala com o provedor.
     */
    enviarMensagem(conversaId: string, texto: string): Promise<Mensagem>;
    /**
     * A conversa daquela pessoa naquele canal — a que já existe, ou uma nova.
     *
     * **Uma linha por (contato, canal).** No WhatsApp o histórico é um só: abrir
     * uma segunda conversa com quem já falou com a clínica partiria a mesma
     * troca em duas caixas, e a recepção responderia sem ver o que foi dito
     * antes. Conversa resolvida é reaberta em vez de duplicada.
     *
     * Devolve o id, que é o que a tela de Comunicação precisa para abrir.
     */
    abrirConversaCom(contatoId: string, canalId: string): Promise<string>;
    /** Assume a conversa em nome de quem está logado. */
    assumirConversa(conversaId: string): Promise<Conversa>;
    /** Resolve, com o motivo quando a pessoa deu um. Quem e quando o servidor registra (0160). */
    resolverConversa(conversaId: string, motivo?: string): Promise<Conversa>;
    /** Reabre a conversa resolvida com quem reabriu como atendente. */
    reabrirConversa(conversaId: string): Promise<Conversa>;
    /**
     * De Abertos para Pendentes, a pedido de quem atende (0161): a conversa
     * perde o responsável e a reserva e continua na fila.
     */
    devolverConversa(conversaId: string): Promise<void>;

    /** Um pipeline, por etapa — o escolhido, ou o primeiro da clínica. */
    pipeline(filtro: { busca?: string; responsavel?: string; funilId?: string }): Promise<CargaDoPipeline>;
    /** Leva o lead à etapa pelo id. Etapa de outro pipeline leva o lead junto. */
    moverLeadDeEtapa(id: string, etapaId: string): Promise<Lead>;

    /** Orçamentos e pacotes do recorte pedido. */
    vendas(filtro: FiltroDeVendas): Promise<CargaDeVendas>;
    criarOrcamento(dados: NovoOrcamento): Promise<Orcamento>;
    /**
     * Muda a situação do orçamento. Aprovar gera o pacote quando houver sessões
     * e as parcelas em contas a receber (`cobranca`); sair de aprovado cancela
     * as parcelas sem pagamento, e é recusado se alguma recebeu.
     */
    decidirOrcamento(id: string, status: StatusDoOrcamento, cobranca?: CondicaoDeCobranca): Promise<Orcamento>;

    /** Títulos, movimentações e cadastros do financeiro. */
    financeiro(filtro: FiltroDoFinanceiro): Promise<CargaDoFinanceiro>;
    /**
     * Registra uma baixa. A situação do título é consequência dos pagamentos,
     * não campo que alguém lembra de atualizar.
     */
    registrarPagamento(dados: NovoPagamento): Promise<Titulo>;
    /** Cria ou renomeia uma categoria de conta. */
    salvarCategoriaDeConta(categoria: { id?: string; nome: string; natureza: 'receita' | 'despesa'; paiId?: string }): Promise<CategoriaDeConta>;
    /**
     * Tira a categoria de circulação sem apagá-la: os títulos já lançados
     * apontam para ela, e apagar deixaria lançamento sem classificação.
     */
    inativarCategoriaDeConta(id: string, ativa: boolean): Promise<void>;

    /** Prontuário do paciente: fichas preenchidas, modelos e consultas. */
    prontuario(contatoId: string): Promise<ProntuarioDoPaciente>;
    /** O resumo de todas as consultas, como está guardado. Não gera. */
    resumoDoPaciente(contatoId: string): Promise<ResumoDoPaciente | null>;
    /** Manda REFAZER o resumo com o que existe hoje. Custa uma chamada de IA. */
    gerarResumoDoPaciente(contatoId: string): Promise<ResumoDoPaciente>;
    /** Modelos da clínica, para o construtor. */
    modelosDeFicha(): Promise<ModeloDeFicha[]>;
    /** Cria ou atualiza um modelo. Alterar campos sobe a versão no banco. */
    salvarModeloDeFicha(modelo: { id?: string; nome: string; campos: CampoDeFicha[] }): Promise<ModeloDeFicha>;
    arquivarModeloDeFicha(id: string): Promise<void>;
    salvarFicha(dados: NovaFicha): Promise<FichaPreenchida>;
    /**
     * Guarda um anexo da ficha e devolve o que a resposta grava.
     *
     * O balde é privado e o caminho começa pela clínica — é assim que a mesma
     * regra de recorte da RLS vale para o arquivo. Nada aqui devolve URL
     * permanente: foto de paciente em link que não vence vaza para sempre.
     */
    enviarAnexoDaFicha(contatoId: string, uid: string, arquivo: File): Promise<ArquivoDaFicha>;
    /** Um endereço temporário para ver o anexo. */
    enderecoDoAnexo(caminho: string): Promise<string>;

    /** A consulta em curso, para o editor de atendimento. */
    atendimentoEmCurso(id: string): Promise<AtendimentoEmCurso>;
    /** Abre a consulta: marca o início, que é o que o cronômetro conta. */
    iniciarAtendimento(id: string): Promise<AtendimentoEmCurso>;
    /**
     * A consulta presencial que começa pela VecchIA (0157): abre agora, com quem
     * pede, para um paciente já cadastrado. Devolve o id do atendimento aberto.
     */
    abrirConsultaPresencial(pacienteId: string): Promise<string>;
    encerrarAtendimento(id: string): Promise<AtendimentoEmCurso>;

    /* ---- nota da consulta pela IA (0155, 0156) ---- */
    /** As notas da consulta que esta pessoa pode ver: as dela e as aprovadas. */
    notasDeIaDaConsulta(atendimentoId: string): Promise<NotaDeIa[]>;
    /** Consulta inteira exige `consentimento`. Uma nota em andamento por pessoa e consulta. */
    iniciarNotaDeIa(atendimentoId: string, modo: ModoDaNota, opcoes: OpcoesDaNovaNota): Promise<NotaDeIa>;
    /** Transcreve uma parte do áudio. O áudio não fica no servidor. Repetir a mesma ordem substitui. */
    enviarParteDaNotaDeIa(notaId: string, parte: { ordem: number; segundos: number; audio: Blob }): Promise<void>;
    /** As partes já transcritas, na ordem. */
    partesDaNotaDeIa(notaId: string): Promise<ParteDaNota[]>;
    /** Encerra a gravação e pede à IA a ficha e o resumo. Devolve a nota em revisão (ou com a falha). */
    gerarNotaDeIa(notaId: string): Promise<NotaDeIa>;
    /** Grava a ficha no prontuário e o resumo, com o que o profissional conferiu. Devolve o id da ficha. */
    aprovarNotaDeIa(notaId: string, respostas: NovaFicha['respostas'], resumo: string): Promise<string>;
    /** Apaga o texto; fica só o rastro de que houve gravação. */
    descartarNotaDeIa(notaId: string): Promise<void>;
    /** Os resumos aprovados do paciente, respeitando quem pode ler (`at.resumo-ia`). */
    resumosDaIaDoPaciente(contatoId: string): Promise<ResumoDaConsulta[]>;

    /** Produtos, cadastros, reservas e movimentações do estoque. */
    estoque(filtro: FiltroDoEstoque): Promise<CargaDoEstoque>;
    /**
     * Cadastra o produto e lança o saldo inicial como movimentação.
     *
     * Saldo que aparece sem movimentação é a primeira linha do extrato a não
     * bater, e ninguém descobre de onde veio.
     */
    criarProduto(dados: NovoProduto): Promise<Produto>;
    /** Altera o cadastro. O saldo NÃO entra aqui: ele só muda por movimentação. */
    atualizarProduto(id: string, dados: DadosDoProduto): Promise<Produto>;
    /** Entrada ou saída. Devolve o produto já com o saldo novo. */
    registrarMovimentacao(dados: NovaMovimentacao): Promise<Produto>;
    salvarFornecedor(dados: DadosDoFornecedor): Promise<Fornecedor>;
    /* ---- fornecedores e prestadores nos Contatos (0159) ---- */
    /** Com `novoTipoServico`, o tipo digitado em "Outros" é criado antes. */
    salvarPrestador(dados: DadosDoPrestador): Promise<PrestadorDeServico>;
    /** Todos os tipos da clínica, ativos e inativos, com quantos prestadores usam cada um. */
    tiposDeServico(): Promise<TipoDeServico[]>;
    salvarTipoDeServico(dados: { id?: string; nome: string; ativo?: boolean }): Promise<TipoDeServico>;
    /** As categorias de despesa ativas, para o cadastro. Sem acesso ao financeiro, vazia. */
    categoriasParaCadastro(): Promise<CategoriaParaEscolha[]>;
    salvarMarca(dados: DadosDaMarca): Promise<Marca>;
    salvarCategoriaDeProduto(dados: DadosDaCategoriaDeProduto): Promise<CategoriaDeProduto>;
    /** Consome a reserva: baixa o saldo e deixa a movimentação de cada insumo. */
    baixarReserva(id: string): Promise<void>;
    /** Devolve os insumos ao disponível sem movimentação — nada saiu do armário. */
    liberarReserva(id: string): Promise<void>;

    /** Catálogo de procedimentos, com custo, margem e o que o estoque suporta. */
    procedimentos(filtro: FiltroDeProcedimentos): Promise<CargaDeProcedimentos>;
    /**
     * Cria ou atualiza o procedimento com insumos, custos extras e
     * profissionais numa chamada só: salvos em pedidos separados, uma falha de
     * rede no meio deixaria o procedimento com o preço novo e o insumo velho.
     */
    salvarProcedimento(dados: DadosDoProcedimento): Promise<Procedimento>;
    /** Reserva os insumos do procedimento — o que uma proposta aceita dispara. */
    reservarInsumos(procedimentoId: string, contexto: ContextoDaReserva): Promise<ReservaDeEstoque>;

    /** Pedidos ao fornecedor e a sugestão de reposição do estoque. */
    compras(filtro: FiltroDeCompras): Promise<CargaDeCompras>;
    /** Cria ou altera o pedido com seus itens. Pedido sem item não é pedido. */
    salvarPedido(dados: DadosDoPedido): Promise<PedidoDeCompra>;
    /**
     * Recebe o que chegou.
     *
     * Cada item recebido vira uma ENTRADA em `movimentacoes_estoque` — o mesmo
     * caminho da entrada manual, com o pedido gravado na linha. Dois caminhos
     * para o mesmo fato deixariam o saldo certo e o extrato incompleto.
     */
    receberPedido(dados: Recebimento): Promise<PedidoDeCompra>;
    /** Cancela o pedido. O que já entrou no estoque continua lá — e no extrato. */
    cancelarPedido(id: string): Promise<void>;

    /** Documentos emitidos e os modelos que os geram. */
    documentos(filtro: FiltroDeDocumentos): Promise<CargaDeDocumentos>;
    /** Cria ou altera um modelo. Publicar é o que o libera para emissão. */
    salvarModeloDeDocumento(dados: DadosDoModeloDeDocumento): Promise<ModeloDeDocumento>;
    duplicarModeloDeDocumento(id: string): Promise<ModeloDeDocumento>;
    /**
     * Tira o modelo de circulação.
     *
     * Modelo já usado é **despublicado**, nunca apagado: os documentos emitidos
     * apontam para ele, e apagá-lo deixaria termo assinado sem origem.
     */
    removerModeloDeDocumento(id: string): Promise<{ removido: boolean }>;
    /**
     * Emite o documento a partir do modelo, com os campos já preenchidos.
     *
     * O texto é congelado aqui: é ele que a paciente assina, e relê-lo pelo
     * modelo vivo reescreveria o que foi assinado.
     */
    emitirDocumento(dados: NovoDocumento): Promise<Documento>;
    /** Registra a assinatura, a recusa ou o cancelamento do documento. */
    decidirDocumento(id: string, status: StatusDoDocumento, motivo?: string): Promise<Documento>;

    /**
     * Os números do período: agenda, atendimento, dinheiro e funil.
     *
     * Uma carga só, e não quatro: as quatro abas leem as mesmas linhas, e
     * quatro consultas separadas dariam quatro fotos de instantes diferentes.
     */
    relatorios(filtro: FiltroDeRelatorios): Promise<CargaDeRelatorios>;

    /**
     * Cadastro do tenant: dados da clínica, unidades, jornada, salas e equipe.
     *
     * Carga única pela mesma razão dos relatórios — as abas leem o mesmo
     * cadastro, e a jornada da aba Agenda é o denominador que a aba Clínica
     * mostra em horas por semana.
     */
    configuracoes(aba: AbaDeConfiguracoes): Promise<CargaDeConfiguracoes>;
    salvarDadosDaClinica(mudancas: EdicaoDaClinica): Promise<DadosDaClinica>;
    salvarUnidade(id: string, mudancas: EdicaoDaUnidade): Promise<UnidadeDaClinica>;
    /**
     * Grava a jornada inteira de uma vez.
     *
     * Faixa a faixa daria estados intermediários que a semana não aceita — o
     * dia com almoço passaria por "aberto o dia inteiro" entre uma gravação e
     * outra, e quem lesse no meio leria jornada que ninguém declarou.
     */
    salvarHorarios(dias: DiaDeFuncionamento[]): Promise<DiaDeFuncionamento[]>;
    /** `id` nulo cria. Sala nunca é apagada: é desativada, porque a agenda antiga aponta para ela. */
    salvarSala(id: string | null, dados: { nome: string; ativa: boolean }): Promise<SalaDaClinica>;

    /**
     * As automações com o alcance de cada uma calculado agora.
     *
     * O alcance é o que separa automação de texto sobre automação: dizer
     * "cobra título vencido" sem dizer quantos estão vencidos hoje é uma
     * promessa que ninguém confere.
     */
    automacoes(aba: AbaDeAutomacoes): Promise<CargaDeAutomacoes>;
    alternarAutomacao(id: string, ativa: boolean): Promise<Automacao>;
    /**
     * Executa a automação AGORA, à mão.
     *
     * Só as de ação interna: as de mensagem dependem de canal pareado, e não há.
     * Executar duas vezes no mesmo dia não duplica tarefa — o índice único por
     * automação e título recusa a segunda.
     */
    executarAutomacao(id: string): Promise<ResultadoDaExecucao>;

    /**
     * Campanhas, públicos e o que cada origem trouxe.
     *
     * O público de cada campanha é avaliado agora, não lido de uma lista
     * gravada: campanha para "quem não volta há 90 dias" mira em gente
     * diferente amanhã.
     */
    marketing(aba: AbaDeMarketing): Promise<CargaDeMarketing>;
    /** `id` nulo cria. */
    salvarCampanha(id: string | null, dados: DadosDaCampanha): Promise<Campanha>;

    /** `id` nulo cria uma linha nova na régua de lembretes. */
    salvarLembrete(id: string | null, dados: DadosDoLembrete): Promise<Lembrete>;
    removerLembrete(id: string): Promise<{ removido: boolean }>;

    /**
     * O painel de quem opera a VecchIA.
     *
     * Devolve CONTAGEM por clínica, nunca linha da operação: a RLS não deixa o
     * operador ler contato nem agenda, e as funções do banco que alimentam esta
     * carga foram escritas para dar número. Ver o que a clínica faz continua
     * exigindo impersonação — registrada, com motivo e prazo.
     */
    plataforma(): Promise<CargaDaPlataforma>;
    clinicaDaPlataforma(id: string): Promise<CargaDaClinicaNaPlataforma>;

    /** Planos, assinaturas e faturas — a cobrança da plataforma. */
    faturamento(): Promise<CargaDoFaturamento>;
    salvarPlano(id: string | null, dados: DadosDoPlano): Promise<Plano>;
    /**
     * Cria as faturas da competência, sem duplicar as que já existem.
     *
     * Só clínica ativa ou inadimplente, e só plano COM preço: fatura de valor
     * nulo seria cobrança de valor que ninguém decidiu.
     */
    gerarFaturas(competencia: string): Promise<{ criadas: number }>;
    /** `pagoEm` nulo desfaz a baixa. */
    baixarFatura(id: string, pagoEm: string | null): Promise<Fatura>;

    /**
     * O checklist de implantação de cada clínica.
     *
     * Derivado do estado do banco, não de etapas marcadas: assim ele se
     * desmarca sozinho quando a coisa deixa de ser verdade.
     */
    onboarding(): Promise<CargaDoOnboarding>;
    suporte(): Promise<CargaDoSuporte>;
    /** `id` nulo abre um chamado novo. */
    salvarChamado(id: string | null, dados: DadosDoChamado): Promise<Chamado>;

    /** As feature flags de módulo, com o padrão e as exceções por clínica. */
    flags(): Promise<CargaDasFlags>;
    /** Muda o padrão da flag, que vale para toda a base. */
    definirPadraoDaFlag(id: string, ativo: boolean): Promise<FlagDeModulo>;
    /** `ativo` nulo remove a exceção e devolve a clínica ao padrão. */
    definirExcecaoDaFlag(id: string, clinicaId: string, ativo: boolean | null): Promise<FlagDeModulo>;

    /* --------------------------------------------------------- integrações */

    /** Os canais da clínica com o estado da credencial — nunca a credencial. */
    canaisDaClinica(): Promise<CanalDeIntegracao[]>;
    salvarCanal(id: string | null, dados: DadosDoCanal): Promise<CanalDeIntegracao>;
    /**
     * Guarda a credencial do canal.
     *
     * Entra e não volta: o segredo é cifrado no Vault por uma função do banco,
     * e nenhuma consulta do aplicativo consegue lê-lo de novo.
     */
    salvarCredencialDoCanal(canalId: string, credenciais: Record<string, string>): Promise<CanalDeIntegracao>;
    removerCredencialDoCanal(canalId: string): Promise<CanalDeIntegracao>;

    /**
     * A plataforma já ligou um servidor de WhatsApp?
     *
     * Sem isso o botão de gerar QR apareceria para dar erro. A clínica não vê
     * qual servidor nem o token — só que dá para parear.
     */
    pareamentoDisponivel(): Promise<boolean>;
    /**
     * Cria a instância, pede um QR novo, consulta o estado ou encerra a sessão.
     *
     * `criar` é o único que fala com o token de administração do provedor, e
     * ele mora na borda: o navegador nunca o vê. Nenhuma ação apaga instância —
     * `desconectar` encerra a sessão do WhatsApp e a instância continua lá,
     * pronta para outro QR.
     */
    parearCanal(canalId: string, acao: AcaoDePareamento, telefone?: string): Promise<PareamentoDoCanal>;
    /**
     * Testa o login SMTP do canal de e-mail com a credencial já salva. Conectado
     * quando o servidor aceita o usuário e a senha; nada é enviado.
     */
    validarCanalDeEmail(canalId: string): Promise<{ status: StatusDoCanal; motivo?: string }>;

    /** Os servidores de canal da plataforma — endereço e dica, jamais o token. */
    provedoresDeCanalDaPlataforma(): Promise<ProvedorDeCanalDaPlataforma[]>;
    salvarProvedorDeCanal(provedor: string, baseUrl: string, token: string): Promise<ProvedorDeCanalDaPlataforma[]>;
    removerProvedorDeCanal(provedor: string): Promise<ProvedorDeCanalDaPlataforma[]>;

    /** As chaves de IA da plataforma — existência e dica, jamais o valor. */
    chavesDeIa(): Promise<ChaveDeIa[]>;
    salvarChaveDeIa(provedor: ProvedorDeIa, chave: string, modelo?: string): Promise<ChaveDeIa[]>;
    removerChaveDeIa(provedor: ProvedorDeIa): Promise<ChaveDeIa[]>;

    /* ----------------------------------------------------------------- IA */

    /**
     * A IA da clínica pode responder agora?
     *
     * Existe chave de IA na plataforma e a clínica está dentro do teto do mês.
     * Os agentes, os fluxos e a base leem isto para dizer, antes do clique, que
     * não dá para testar — em vez de responder erro a cada tentativa.
     */
    iaDisponivel(): Promise<boolean>;
    /**
     * O consumo de IA dos agentes, dos fluxos e da base, por clínica e modelo.
     *
     * Números, como todo o resto do painel: o TEXTO das perguntas não é
     * guardado — pergunta de clínica é dado da clínica.
     */
    consumoDeIa(dias?: number): Promise<LinhaDeConsumoDeIa[]>;

    /** A base de conhecimento desta clínica. */
    baseDaClinica(): Promise<DocumentoDaBase[]>;

    /**
     * Põe um documento na base e manda prepará-lo.
     *
     * `texto` para o que foi digitado na tela; `arquivo` para o que foi enviado.
     * Volta assim que o documento existe — a preparação continua no servidor, e
     * é a `situacao` que diz quando ele passa a responder.
     */
    adicionarNaBase(entrada: { titulo: string; texto?: string; arquivo?: File }): Promise<DocumentoDaBase>;

    /**
     * Refaz o fatiamento e a vetorização.
     *
     * `texto` só na criação do documento digitado — no reprocessamento o texto
     * vem do balde, e mandá-lo de novo do navegador seria trafegar o mesmo
     * conteúdo duas vezes.
     */
    prepararDocumentoDaBase(id: string, texto?: string): Promise<DocumentoDaBase>;

    removerDaBase(id: string): Promise<void>;

    /** Os tetos de todas as clínicas. Painel da plataforma. */
    orcamentosDeIa(): Promise<OrcamentoDeIa[]>;

    /** Define (ou remove, com `null`) o teto de uma clínica. Só operador. */
    salvarOrcamentoDeIa(clinicaId: string, limite: number | null): Promise<void>;

    /**
     * O teto da própria clínica.
     *
     * Existe para a tela poder dizer POR QUE a IA parou. Parar sem
     * explicação faz a clínica abrir chamado achando que quebrou.
     */
    meuOrcamentoDeIa(): Promise<MeuOrcamentoDeIa | null>;

    /**
     * Os caminhos que o servidor realmente publica, para conferir o catálogo.
     *
     * Documentação que mente é pior que documentação que falta: a tela compara
     * o que está escrito com o que o PostgREST responde e avisa o que sumiu.
     */
    caminhosDaApi(): Promise<string[]>;

    /**
     * Pacientes atendidos há mais de `dias` e sem nada marcado.
     *
     * A lista com nome e telefone fica NA TELA de quem vai ligar: follow-up
     * sem o nome de quem ligar é sugestão que não vira ligação.
     */
    retornosEmAberto(dias?: number): Promise<RetornoEmAberto[]>;

    /* ------------------------------------------------------------ agentes */

    /** Os agentes da clínica que falam com o paciente: atendimento e pré-atendimento. */
    agentes(): Promise<Agente[]>;
    /** `id` nulo cria. */
    salvarAgente(id: string | null, dados: DadosDoAgente): Promise<Agente>;
    removerAgente(id: string): Promise<{ removido: boolean }>;

    /**
     * Roda o agente sobre uma conversa.
     *
     * As ferramentas de leitura executam com o token de quem chamou — a RLS
     * recorta. As de escrita voltam como proposta.
     */
    conversarComAgente(pedido: { tipo: 'atendimento' | 'pre-atendimento'; mensagens: MensagemDoAgente[] }): Promise<RespostaDoAgente>;
    /**
     * Executa uma proposta depois da confirmação de uma pessoa.
     *
     * Passa pelos mesmos caminhos que a tela usaria — criar agendamento, criar
     * tarefa, salvar lembrete —, então a RLS e as validações valem igual.
     */
    confirmarPropostaDoAgente(proposta: PropostaDoAgente): Promise<{ descricao: string }>;
    /** As ferramentas que a função de borda sabe executar, para conferir o catálogo. */
    ferramentasDaBorda(): Promise<{ id: string; escreve: boolean }[]>;

    /* -------------------------------------------------------------- fluxos */

    fluxos(): Promise<Fluxo[]>;
    salvarFluxo(id: string | null, dados: DadosDoFluxo): Promise<Fluxo>;
    removerFluxo(id: string): Promise<{ removido: boolean }>;
    /**
     * Roda o fluxo em seco: calcula o alcance de verdade, aplica as condições e
     * diz o que cada ação faria — sem escrever nada.
     */
    simularFluxo(id: string): Promise<SimulacaoDoFluxo>;
    /**
     * Executa as ações internas do fluxo para quem passou nas condições.
     *
     * Só as internas: enviar mensagem depende de canal pareado, e esperar
     * depende de agendador. A tela confirma antes, com a contagem à vista. O
     * passo de agente roda e devolve RASCUNHOS: o que ele propõe só acontece
     * depois de uma pessoa confirmar.
     */
    executarFluxo(id: string): Promise<ResultadoDoFluxo>;
    /** Pede à IA um rascunho de fluxo a partir de uma frase. */
    gerarFluxoComIa(descricao: string): Promise<DadosDoFluxo>;

    /* ---- trilha T5a ---- */

    /** As preferências que a clínica mudou. O que não está aqui vale o padrão do catálogo. */
    preferencias(): Promise<PreferenciaSalva[]>;
    /**
     * Grava as alteradas e APAGA as restauradas (`valor` nulo).
     *
     * Gravar o padrão como se fosse escolha congelaria a clínica nele: quando o
     * catálogo mudasse o padrão, ela não acompanharia.
     */
    salvarPreferencias(mudancas: { chave: string; valor: ValorDePreferencia | null }[]): Promise<PreferenciaSalva[]>;

    /** Departamentos, filas, etiquetas e mensagens rápidas — o que a Comunicação usa. */
    atendimentoECrm(): Promise<CargaDeAtendimentoECrm>;
    /** `id` nulo cria. Departamento não se apaga: se desativa. */
    salvarDepartamento(id: string | null, dados: DadosDoDepartamento): Promise<{ id: string }>;
    /** Salva a fila e acerta quem a atende, tirando só quem saiu e pondo só quem entrou. */
    salvarFila(id: string | null, dados: DadosDaFila): Promise<{ id: string }>;
    salvarEtiqueta(id: string | null, dados: DadosDaEtiqueta): Promise<{ id: string }>;
    /** O autor sai da sessão, nunca do navegador. */
    salvarMensagemRapida(id: string | null, dados: DadosDaMensagemRapida): Promise<{ id: string }>;
    removerMensagemRapida(id: string): Promise<void>;
    /** Conta um uso. Devolve a contagem nova. */
    usarMensagemRapida(id: string): Promise<number>;

    categoriasDeProcedimento(): Promise<CategoriaDeProcedimento[]>;
    /** `id` nulo cria. Renomear leva o texto dos procedimentos junto, numa transação. */
    salvarCategoriaDeProcedimento(id: string | null, dados: { nome: string; ativa: boolean }): Promise<{ id: string }>;
    /** Categoria em uso não se remove: devolve `removida: false`, e a tela oferece desativar. */
    removerCategoriaDeProcedimento(id: string): Promise<{ removida: boolean }>;

    /** O que a clínica vende — não o que o paciente comprou. */
    pacotesDoCatalogo(): Promise<PacoteDoCatalogo[]>;
    salvarPacoteDoCatalogo(id: string | null, dados: DadosDoPacoteDoCatalogo): Promise<{ id: string }>;

    /** Plano, próxima cobrança e faturas da clínica aberta. Só leitura. */
    assinaturaDaClinica(): Promise<AssinaturaVistaPelaClinica>;

    /**
     * O que o filtro inteligente sabe de cada conversa: fila, etiquetas, agenda,
     * funil e propostas da pessoa. Por id da conversa.
     */
    sinaisDaCaixa(): Promise<Record<string, SinaisDaConversa>>;
    /** Aplica a etiqueta à mão. Já aplicada não é erro. */
    etiquetarConversa(conversaId: string, etiquetaId: string): Promise<void>;
    desetiquetarConversa(conversaId: string, etiquetaId: string): Promise<void>;
    /**
     * A mesma etiqueta, agora na PESSOA (0168) — paciente, cliente ou lead, que
     * aqui são todos contato. Já aplicada não é erro, pelo mesmo motivo: quem
     * clica quer o estado final, não uma transação.
     */
    etiquetarContato(contatoId: string, etiquetaId: string): Promise<void>;
    desetiquetarContato(contatoId: string, etiquetaId: string): Promise<void>;
    /**
     * Manda a conversa para outra fila e, se quiser, para uma pessoa DESSA fila.
     * Com pessoa, a conversa passa a ser da equipe, como em "assumir".
     */
    // ------------------------------------------------ Meta Ads (0162) ---
    /** O painel da aba Anúncios: a conta ligada e os números do período. */
    painelDeAnuncios(dias: number): Promise<CargaDoPainelDeAnuncios>;
    /** As contas de anúncio da clínica (uma por plataforma). */
    contasDeAnuncio(): Promise<ContaDeAnuncioDaClinica[]>;
    /** As campanhas conhecidas, para escolher no lead. Vazio sem conta ou sem permissão. */
    campanhasDeAnuncio(): Promise<CampanhaDeAnuncio[]>;
    /** Começa o login da Meta: devolve o endereço para onde levar a pessoa. */
    conectarMeta(destino: string): Promise<string>;
    /** As contas que o login da Meta alcança. */
    contasDaMeta(): Promise<ContaDaMeta[]>;
    /** Escolhe a conta e lê os primeiros números. */
    escolherContaDaMeta(contaId: string): Promise<void>;
    /** Lê agora os últimos 30 dias. */
    sincronizarMeta(): Promise<{ campanhas: number; linhas: number }>;
    /** Desconecta: apaga o token e os números; os leads ficam, sem a campanha. */
    desconectarMeta(): Promise<void>;
    /** Painel da plataforma: o app da Meta. */
    appDaMeta(): Promise<AppDaMetaDaPlataforma | null>;
    salvarAppDaMeta(appId: string, segredo: string, configId?: string): Promise<AppDaMetaDaPlataforma | null>;
    removerAppDaMeta(): Promise<void>;
    transferirConversa(conversaId: string, filaId: string, atendenteId?: string): Promise<void>;

    /** Contas e formas de pagamento COM as inativas: é o cadastro, e não a lista de escolha. */
    cadastrosFinanceiros(): Promise<CadastrosFinanceiros>;
    salvarConta(id: string | null, dados: DadosDaConta): Promise<{ id: string }>;
    salvarMetodo(id: string | null, dados: DadosDoMetodo): Promise<{ id: string }>;
    /** Título avulso: a receber com paciente, a pagar com fornecedor. */
    criarTitulo(novo: NovoTitulo): Promise<{ id: string }>;
    detalheDoTitulo(id: string): Promise<DetalheDoTitulo>;
    /** O valor não desce abaixo do que já foi pago. */
    editarTitulo(id: string, edicao: EdicaoDeTitulo): Promise<void>;
    /** Só título sem pagamento: cancelar apagaria do histórico dinheiro que andou. */
    cancelarTitulo(id: string): Promise<void>;
    /**
     * Desfaz uma baixa. O pagamento sai, a cópia fica em `pagamentos_estornados`
     * com o motivo e quem estornou, e a situação do título volta pelos pagamentos
     * que restam. Pagamento conciliado devolve a linha do extrato à fila.
     */
    estornarPagamento(pagamentoId: string, motivo: string): Promise<void>;

    /* ---- nota fiscal (0176) e o emissor Focus NFe (0177) ---- */
    /** A configuração fiscal de quem emite — a clínica da sessão, ou a plataforma. Nula se não há. */
    emitenteFiscal(alvo: AlvoFiscal): Promise<EmitenteFiscal | null>;
    /** Campo ausente fica como está; "" apaga. Formato errado volta como erro, com a frase. */
    salvarEmitenteFiscal(alvo: AlvoFiscal, dados: DadosDoEmitente): Promise<EmitenteFiscal>;
    /**
     * A borda abre o .pfx com a senha, confere validade e CNPJ, e guarda no Vault.
     * Com a empresa já ligada ao emissor, o certificado novo vai para lá também;
     * se o emissor recusar, `avisoDoEmissor` diz o porquê (o certificado ficou guardado).
     */
    enviarCertificadoFiscal(alvo: AlvoFiscal, arquivo: Blob, senha: string): Promise<CertificadoFiscal & { avisoDoEmissor?: string }>;
    /** Cadastra (ou atualiza) a empresa no emissor, com o certificado. Devolve o padrão de NFS-e do município. */
    ligarEmissorFiscal(alvo: AlvoFiscal): Promise<{ padrao: PadraoNfse }>;
    removerCertificadoFiscal(alvo: AlvoFiscal): Promise<void>;
    notasFiscais(filtro: FiltroDeNotas): Promise<NotaFiscal[]>;
    /** Rascunho da nota do título a receber; sem valor, o que falta de nota no título. */
    prepararNotaDoTitulo(tituloId: string, valorCentavos?: number, descricao?: string): Promise<{ id: string }>;
    /** Rascunho da nota da fatura: a plataforma emite, a clínica é a tomadora. */
    prepararNotaDaFatura(faturaId: string): Promise<{ id: string }>;
    /** Troca descrição e valor, e relê tomador e serviço dos cadastros. */
    atualizarRascunhoDaNota(id: string, mudanca: { descricao?: string; valorCentavos?: number }): Promise<void>;
    descartarRascunhoDaNota(id: string): Promise<void>;
    /**
     * Envia ao emissor. Recusada com a frase do porquê enquanto houver pendência
     * ou a empresa não estiver ligada; aceita, a nota segue "na prefeitura" até a
     * resposta, que o servidor busca sozinho.
     */
    solicitarEmissaoDaNota(id: string): Promise<void>;
    /** Pede o cancelamento da nota autorizada; o motivo vai à prefeitura (15 a 255 caracteres). */
    cancelarNotaFiscal(id: string, motivo: string): Promise<void>;
    /** Transferências até o fim do mês informado — ou todas. */
    transferencias(mes?: string): Promise<Transferencia[]>;
    transferirEntreContas(nova: NovaTransferencia): Promise<{ id: string }>;
    extratoImportado(contaId: string): Promise<LinhaDoExtratoImportada[]>;
    /** Grava as linhas do OFX. O mesmo FITID na mesma conta não entra de novo. */
    importarExtrato(contaId: string, linhas: LinhaDoOfx[]): Promise<{ novas: number; repetidas: number }>;
    /** Vincula a linha ao título: vira pagamento, e a linha fica conciliada. */
    conciliarLinhaDoExtrato(linhaId: string, tituloId: string): Promise<void>;
    ignorarLinhaDoExtrato(linhaId: string): Promise<void>;

    /* ---- trilha T5a: editor de atendimento ---- */
    /** As pausas da consulta, na ordem. O cronômetro desconta todas. */
    pausasDoAtendimento(atendimentoId: string): Promise<PausaDoAtendimento[]>;
    /** Só consulta aberta pausa, e uma pausa de cada vez. */
    pausarAtendimento(atendimentoId: string): Promise<void>;
    retomarAtendimento(atendimentoId: string): Promise<void>;
    /**
     * Devolve ao atendimento a consulta encerrada HOJE, com o motivo na
     * auditoria. A de outro dia não reabre: o que faltou vira adendo, não
     * reescrita do prontuário.
     */
    reabrirAtendimento(atendimentoId: string, motivo: string): Promise<void>;
    /** A consulta aberta de quem está logado — é o que acende a pílula global. */
    atendimentoEmAndamento(): Promise<AtendimentoEmAndamento | null>;
    /** O rascunho é de quem escreveu: ninguém mais o lê. */
    rascunhoDaFicha(atendimentoId: string, modeloId: string): Promise<RascunhoDaFicha | null>;
    salvarRascunhoDaFicha(atendimentoId: string, modeloId: string, respostas: NovaFicha['respostas']): Promise<RascunhoDaFicha>;
    descartarRascunhoDaFicha(atendimentoId: string, modeloId: string): Promise<void>;

    /* ---- trilha T5a: orçamento ---- */
    /** Um orçamento com os itens — o detalhe que a URL abre. */
    orcamento(id: string): Promise<Orcamento>;
    /**
     * Reescreve itens, responsável e validade do rascunho ou do enviado, numa
     * transação. O número não muda; o enviado volta a rascunho, porque a
     * paciente tem a versão anterior. Aprovado, recusado e expirado não se
     * editam — duplica-se.
     */
    editarOrcamento(id: string, dados: NovoOrcamento): Promise<Orcamento>;

    /* ---- trilha T5a: planos de saúde ---- */
    /** Todos os planos, com os inativos e quantos pacientes têm cada um na ficha. */
    planosDeSaude(): Promise<PlanoDeSaude[]>;
    /** `id` nulo cria. Plano não se exclui — desativa: pacientes e orçamentos apontam para ele. */
    salvarPlanoDeSaude(id: string | null, dados: DadosDoPlanoDeSaude): Promise<{ id: string }>;

    /* ---- trilha T5a: ficha preenchida ---- */
    /**
     * Corrige as RESPOSTAS de uma ficha já salva, com motivo. O modelo do dia, a
     * versão, o atendimento e o paciente não mudam (`0042`); o que mudou e o
     * motivo ficam na auditoria (`0086`).
     */
    alterarRespostasDaFicha(fichaId: string, respostas: NovaFicha['respostas'], motivo: string): Promise<FichaPreenchida>;
    /** Nome, documento e endereço que o cabeçalho da ficha impressa leva. */
    cabecalhoDaClinica(): Promise<CabecalhoDaClinica>;

    /* ---- trilha T2 ---- */

    /** Profissionais, salas, procedimentos e jornada, por id, para o novo agendamento. */
    opcoesDoAgendamento(): Promise<OpcoesDoAgendamento>;
    /** Pacientes, leads e contatos que batem com o nome ou o telefone digitado. */
    buscarContatos(busca: string): Promise<ContatoParaEscolha[]>;
    /** Um contato pelo id — o telefone é o que "Ligar" precisa. */
    contatoPorId(id: string): Promise<ContatoParaEscolha | null>;
    /**
     * Liga ou desliga um lembrete da régua SÓ para este agendamento. Devolve a
     * régua do agendamento já com a exceção aplicada.
     */
    definirLembreteDoAgendamento(agendamentoId: string, lembreteId: string, ativo: boolean): Promise<Lembrete[]>;
    /** A conta de quem está na sessão. */
    minhaConta(): Promise<MinhaConta>;
    /** Altera o próprio nome e telefone — nunca o de outra pessoa. */
    salvarMinhaConta(dados: { nome: string; telefone: string }): Promise<MinhaConta>;
    salvarMinhaPresenca(presenca: MinhaConta['presenca']): Promise<MinhaConta>;
    /** Grava as linhas conferidas da planilha. Cada falha volta com o número da linha. */
    importarContatos(tipo: TipoImportavel, linhas: LinhaImportada[]): Promise<ResultadoDaImportacao>;
    /** Troca o profissional responsável de vários pacientes. Devolve quantos mudaram. */
    trocarProfissionalDosPacientes(contatoIds: string[], profissionalId: string): Promise<number>;
    /**
     * A mesma etiqueta em várias pessoas de uma vez (0168). Devolve quantas
     * GANHARAM a etiqueta: quem já a tinha não conta, e é isso que a tela diz —
     * "12 de 20" com 8 que já estavam é informação, não falha.
     */
    etiquetarContatos(contatoIds: string[], etiquetaId: string): Promise<number>;
    trocarResponsavelDosLeads(leadIds: string[], membroId: string): Promise<number>;
    moverLeadsDeEtapa(leadIds: string[], etapaId: string): Promise<number>;
    /** "Registrar contato": o que foi conversado entra no histórico da pessoa. */
    registrarNoHistoricoDoContato(contatoId: string, texto: string): Promise<void>;
    /** As origens que a clínica já usa, para sugerir ao editar um lead. */
    origensDeLead(): Promise<string[]>;
    /**
     * Arquiva o contato: sai das listas e da busca, e o histórico continua.
     * Apagar prontuário não é opção — arquivar é o que a clínica pode fazer.
     */
    arquivarContato(contatoId: string): Promise<void>;
    /**
     * Dados da clínica e endereço da unidade numa transação só: ou os dois
     * entram, ou nada muda. Nome fantasia, documento e telefone da unidade são
     * obrigatórios.
     */
    salvarDadosDaClinicaEUnidade(clinica: EdicaoDaClinica, unidade?: { id: string; mudancas: EdicaoDaUnidade }): Promise<void>;

    /* ---- trilha T5b ---- */

    /**
     * Abre uma clínica nova: o tenant, a unidade principal, a proprietária como
     * convite pendente e a assinatura — numa transação só, ou nada.
     */
    criarClinica(dados: DadosDaNovaClinica): Promise<{ clinicaId: string }>;
    /** Tira o acesso de todos os membros. Os dados ficam; o motivo é obrigatório. */
    suspenderClinica(id: string, motivo: string): Promise<void>;
    /** Devolve a clínica à situação de antes da suspensão — e diz qual foi. */
    reativarClinica(id: string, motivo: string): Promise<SituacaoDaClinica>;
    /** Muda o plano da clínica e o da assinatura juntos. */
    trocarPlanoDaClinica(id: string, planoCodigo: string, motivo: string): Promise<void>;
    /**
     * Vagas de acesso liberadas para uma clínica, acima ou abaixo do plano
     * (0165). Nulo devolve o número do plano — sem inventar um valor que
     * depois ninguém sabe de onde veio. Devolve quantas estão ocupadas.
     */
    definirLimiteDeMembros(clinicaId: string, limite: number | null): Promise<number>;
    /** Percentual sobre o preço, a partir da competência de hoje. Substitui o vigente. */
    aplicarDesconto(clinicaId: string, dados: DadosDoDesconto): Promise<void>;
    removerDesconto(clinicaId: string, motivo: string): Promise<void>;
    /** Registro interno: nenhuma cobrança sai daqui, porque não há gateway. */
    emitirFaturaAvulsa(dados: DadosDaFaturaAvulsa): Promise<Fatura>;
    /** Registro de que o valor de uma fatura PAGA foi devolvido por fora. */
    estornarFatura(id: string, motivo: string): Promise<Fatura>;
    /** `id` nulo concede; com id, edita crédito que ainda não abateu fatura nenhuma. */
    salvarCredito(clinicaId: string, id: string | null, dados: DadosDoCredito): Promise<CreditoDaClinica>;
    /** O saldo zera e sai da próxima fatura; o registro fica. */
    excluirCredito(id: string, motivo: string): Promise<void>;
    /** `null` devolve a flag ao padrão da base. */
    definirRolloutDaFlag(id: string, percentual: number | null): Promise<FlagDeModulo>;
    /** Desliga — ou religa — o módulo para TODAS as clínicas, exceções inclusive. */
    acionarKillSwitch(id: string, desligar: boolean, motivo: string): Promise<FlagDeModulo>;
    /** A equipe da plataforma, com permissões e o estado do MFA. */
    operadoresDaPlataforma(): Promise<OperadorDaPlataforma[]>;
    definirPermissoesDoOperador(usuarioId: string, permissoes: string[]): Promise<OperadorDaPlataforma[]>;
    /** O que o banco mede de si. Nulo na demonstração, que não tem banco. */
    saudeDoBanco(): Promise<SaudeDoBanco | null>;
    /** As chaves de API da clínica ativa — prefixo e uso, nunca a chave. */
    chavesDeApi(): Promise<ChaveDeApi[]>;
    /** A chave inteira volta UMA vez, nesta resposta, e não existe no banco. */
    criarChaveDeApi(nome: string): Promise<ChaveDeApiCriada>;
    revogarChaveDeApi(id: string): Promise<ChaveDeApi[]>;

    /* ---- trilha T4 ---- */
    /**
     * A base de conhecimento consegue vetorizar? Só existência e provedor —
     * nunca a chave. Sem chave OpenAI ou Gemini, documento novo não vira base, e
     * a tela diz isso antes de a clínica subir um PDF.
     */
    baseDeConhecimentoDisponivel(): Promise<{ disponivel: boolean; provedor?: string }>;
    /**
     * Envia (ou reenvia) o convite por e-mail ao profissional que ainda não tem
     * conta. Devolve o e-mail para onde saiu; nulo na demonstração, onde nenhum
     * e-mail sai.
     */
    convidarMembro(membroId: string): Promise<{ email: string | null }>;
    /**
     * Cria a pessoa da equipe JÁ com login e senha, ativa — sem convite por
     * e-mail. Só quem gerencia a equipe; a recusa é do banco. Sem vaga no
     * plano (0165), o banco recusa aqui também.
     */
    criarUsuarioDaEquipe(novo: NovoUsuarioDaEquipe): Promise<{ id: string; email: string }>;
    /**
     * Define a senha de quem já tem login, sem e-mail. A própria senha, a da
     * proprietária e a de conta usada em outra clínica ficam fora: a recusa é
     * da borda (`equipe-usuario`).
     */
    definirSenhaDoMembro(membroId: string, senha: string): Promise<void>;
    /**
     * A clínica autorizou o agendador a mandar lembrete, automação e fluxo pelo
     * WhatsApp sozinho? Desligado por padrão.
     */
    enviosAutomaticos(): Promise<boolean>;
    /** Liga ou desliga o envio automático. Só quem tem a permissão de configurações. */
    definirEnviosAutomaticos(ligado: boolean): Promise<boolean>;
    /** Quem a campanha alcança agora — a mesma conta do "Público agora". */
    publicoDaCampanha(id: string): Promise<{ contatoId: string; nome: string }[]>;
    /**
     * Põe a campanha na fila do agendador, para o público de agora. Uma pessoa
     * confirma antes, com a contagem à vista; o banco cobra permissão, canal
     * conectado, limite e consentimento.
     */
    dispararCampanha(id: string, mensagem: string): Promise<{
        enfileirados: number; jaNaFila: number; semTelefone: number; semConsentimento: number; foraDaClinica: number;
    }>;
    /** Imagem ou PDF na conversa, pelo WhatsApp conectado. */
    enviarArquivo(conversaId: string, arquivo: File, legenda?: string): Promise<Mensagem>;

    /* ---- trilha T7 ---- */

    /** Os pipelines ativos da clínica, cada um com todas as etapas (as arquivadas, marcadas). */
    funis(): Promise<Funil[]>;
    /** Pipeline novo por um dos modelos do protótipo. Só com a permissão de configurações. */
    criarFunil(dados: { nome: string; modelo: ModeloDeFunil; responsavelPadraoId?: string }): Promise<Funil>;
    /**
     * Nome, títulos, cores e ordem numa gravação só. `etapas` é a lista COMPLETA das
     * ativas, na ordem; item sem id é etapa aberta nova. Tirar etapa é arquivar.
     */
    salvarFunil(funilId: string, dados: { nome?: string; etapas: EtapaEditada[] }): Promise<Funil>;
    /** Arquiva etapa aberta; com leads nela, o destino é obrigatório. Devolve quantos leads mudaram. */
    arquivarEtapa(etapaId: string, destinoId?: string): Promise<number>;

    /** As unidades ativas, por nome: as do seletor da topbar e dos formulários. */
    unidadesAtivas(): Promise<UnidadeDaClinica[]>;
    /** Unidade nova, com auditoria. Só com a permissão de configurações. */
    criarUnidade(dados: NovaUnidade): Promise<UnidadeDaClinica>;
    /**
     * Arquiva, com auditoria. O servidor recusa a última unidade ativa e a unidade com
     * agendamento futuro, dizendo qual das duas travas barrou.
     */
    arquivarUnidade(id: string): Promise<void>;

    /* ---- trilha T6 ---- */
    /** Chamadas por chave e por dia, dos últimos `dias` (1 a 90). Só a proprietária. */
    usoDasChavesDeApi(dias?: number): Promise<UsoDeChaveDeApi[]>;
    /** Endereço público do logotipo da clínica ativa, ou nulo para a marca padrão. */
    logoDaClinica(): Promise<string | null>;
    /** PNG, JPEG ou WebP até 2 MB. Devolve o endereço público. */
    enviarLogoDaClinica(arquivo: File): Promise<string>;
    removerLogoDaClinica(): Promise<void>;
    /** O operador manda o convite da proprietária de uma clínica que ele abriu. */
    convidarProprietaria(clinicaId: string): Promise<{ email: string | null }>;
    /** Ligado, escrever no painel exige sessão com o segundo fator. */
    mfaObrigatorioParaEscrita(): Promise<boolean>;
    definirMfaObrigatorioParaEscrita(ligado: boolean): Promise<boolean>;

    /* ---- trilha T9 ---- */
    /** Os gateways configurados. A chave e o segredo não voltam: só a existência e os 4 últimos. */
    gatewaysDePagamento(): Promise<GatewayDePagamento[]>;
    /** Quem edita as integrações. A chave e o segredo vão para o cofre. */
    salvarGateway(provedor: ProvedorDeGateway, dados: DadosDoGateway): Promise<void>;
    /** `null` desliga a cobrança online; as faturas já cobradas continuam no gateway delas. */
    definirGatewayAtivo(provedor: ProvedorDeGateway | null): Promise<void>;
    removerGateway(provedor: ProvedorDeGateway): Promise<void>;
    /** Uma leitura barata no gateway, só quando o operador pede. */
    testarGateway(provedor: ProvedorDeGateway): Promise<TesteDoGateway>;
    /** Cria (ou recria) a cobrança da fatura no gateway ativo. */
    criarCobranca(faturaId: string): Promise<CobrancaDaFatura>;
    /** As faturas em aberto sem cobrança, no gateway ativo — depois de gerar a competência ou a avulsa. */
    criarCobrancasPendentes(): Promise<CobrancasCriadas>;
    cancelarCobranca(faturaId: string): Promise<void>;
    /** Onde a clínica paga a própria fatura. Cria a cobrança se ainda não houver, ou se o link venceu. */
    linkDePagamento(faturaId: string): Promise<CobrancaDaFatura>;
    /** A fatura vencida da clínica aberta, ou `null` se está em dia. */
    minhaInadimplencia(): Promise<InadimplenciaDaClinica | null>;
    /**
     * O teste do autocadastro (0150) da clínica aberta, para o aviso no topo — ou
     * `null` se ela não está em teste. Leitura leve: roda a cada volta à aba.
     */
    meuTeste(): Promise<{ testeAte: string; plano: string; precoCentavos: number | null; cartaoFinal: string | null; cancelado: boolean } | null>;

    /* ---- central de notificações (0151) ---- */
    /** Os avisos não lidos de quem está na sessão, na clínica aberta, do mais novo ao mais antigo. */
    minhasNotificacoes(): Promise<Notificacao[]>;
    /** Quantos não lidos — o número do sininho. Leve: roda a cada minuto. */
    contarNotificacoes(): Promise<number>;
    /** Lido sai da central. */
    marcarNotificacaoLida(id: string): Promise<void>;
    marcarTodasNotificacoesLidas(): Promise<void>;

    /* ---- Google Agenda (0152) ---- */
    /** A agenda Google de quem está na sessão, ou nula se não conectou. */
    minhaAgendaGoogle(): Promise<AgendaGoogle | null>;
    /** O endereço do Google para autorizar; a tela vai para ele e volta para `destino`. */
    conectarAgendaGoogle(destino: string): Promise<string>;
    /** Os eventos de hoje em diante saem do Google; a conexão se apaga quando a fila terminar. */
    desconectarAgendaGoogle(): Promise<void>;

    /* ---- Webhooks de saída (0153) ---- */
    /** Os webhooks da clínica ativa. Vazio para quem não tem a permissão de configurações. */
    webhooks(): Promise<Webhook[]>;
    /** O segredo de assinatura vem uma vez, nesta resposta. */
    criarWebhook(dados: DadosDoWebhook): Promise<WebhookCriado>;
    atualizarWebhook(id: string, dados: DadosDoWebhook): Promise<Webhook[]>;
    /** Troca o segredo na hora: o destino precisa do novo para conferir a assinatura. */
    girarSegredoDoWebhook(id: string): Promise<string>;
    excluirWebhook(id: string): Promise<Webhook[]>;
    /** Põe uma entrega de teste na fila; a borda envia em até 30 segundos. */
    testarWebhook(id: string): Promise<void>;
    entregasDoWebhook(id: string): Promise<EntregaDeWebhook[]>;
    /** Reenvia como entrega nova, com outro `webhook-id`. */
    reenviarEntregaDeWebhook(id: string): Promise<void>;

    /* ---- trilhas B4 e B5: brain ---- */
    /** Verdadeiro na demonstração: a tela avisa que as respostas são simuladas. */
    brainDemonstracao(): boolean;
    /**
     * As conversas de quem está logado — privadas por usuário —, as mais recentes
     * primeiro. Com `projetoId`, só as daquele projeto; `null`, só as fora de projeto.
     */
    brainConversas(filtro?: { projetoId: string | null }): Promise<ConversaDoBrain[]>;
    brainMensagens(conversaId: string): Promise<MensagemDoBrain[]>;
    /**
     * Sem `conversaId`, abre conversa nova. Proposta volta pendente: nada executa
     * aqui. O `sinal` para a espera na tela; o servidor termina e guarda pelo `pedidoId`.
     */
    brainEnviar(envio: EnvioAoBrain, sinal?: AbortSignal): Promise<RespostaDoBrain>;
    /** Reencontra a resposta de um envio depois de queda de conexão ou de parar. */
    brainPedido(pedidoId: string): Promise<PedidoDoBrain>;
    /** Sobe ao balde privado `brain`, recusando tipo e tamanho antes da subida. */
    brainEnviarAnexo(arquivo: File, conversaId?: string): Promise<AnexoDoBrain>;
    /** A única porta por onde uma ação do Brain executa — e só por clique de quem vê os campos. */
    brainConfirmarProposta(id: string): Promise<PropostaDoBrain>;
    brainCancelarProposta(id: string): Promise<PropostaDoBrain>;
    brainApagarConversa(id: string): Promise<void>;
    brainRenomearConversa(id: string, titulo: string): Promise<void>;
    /** Põe a conversa num projeto da própria pessoa, ou tira (`null`). */
    brainMoverConversa(id: string, projetoId: string | null): Promise<void>;
    brainProjetos(): Promise<ProjetoDoBrain[]>;
    brainCriarProjeto(nome: string): Promise<ProjetoDoBrain>;
    brainRenomearProjeto(id: string, nome: string): Promise<void>;
    /** "Apagar" projeto é arquivar: as conversas ficam. */
    brainArquivarProjeto(id: string): Promise<void>;
    /** Sem linha no servidor, o padrão. */
    brainPreferencias(): Promise<PreferenciasDoBrain>;
    brainSalvarPreferencias(preferencias: PreferenciasDoBrain): Promise<PreferenciasDoBrain>;
    /** Só os provedores com chave ativa na plataforma. */
    brainModelos(): Promise<ModelosDoBrain>;
    /** IA Prompts do campo de mensagem: gramática, melhorar, escrever e traduzir. */
    brainTexto(acao: AcaoDeTexto, texto: string, idioma?: IdiomaDoBrain): Promise<string>;
    /** O texto de uma página, lido pelo servidor, para anexar à mensagem. */
    brainAprenderLink(url: string): Promise<LinkAprendido>;
    brainTranscrever(audio: Blob, idioma?: IdiomaDoBrain): Promise<string>;
    /** Com `fluxo`, a fala recém-gerada chega em `FalaGerada.fluxo`, tocável antes de terminar. */
    brainFalar(texto: string, opcoes?: { idioma?: IdiomaDoBrain; fluxo?: boolean }): Promise<FalaGerada>;
    /** Endereço assinado e curto do anexo ou do arquivo gerado. */
    brainAbrirArquivo(caminho: string): Promise<string>;
}

/* Com servidor configurado, os dados vêm dele; sem, o de demonstração mantém a
   aplicação e os testes de pé. A troca é aqui, e em nenhuma tela. */
let porta: PortaDeDados = temSupabase() ? adaptadorSupabase : adaptadorDeDemonstracao;

export function dados(): PortaDeDados {
    return porta;
}

/** Troca o adaptador. Um lugar só, chamado no arranque ou pelos testes. */
export function definirPorta(nova: PortaDeDados): void {
    porta = nova;
}
